import { action, observable, reaction, makeObservable } from 'mobx';
import {
  contentLookupService,
  SuccessResponse,
  TimeZone,
  timeZoneLoaders,
  User,
  userLoaders,
  VersionedContentLookup
} from '../../../domain';
import {
  Disposers,
  HttpPromiseState,
  logger,
  PromiseState,
  toastrHelper
} from '../../../lib/helpers';
import {routes} from '../../../pages/Routes';
import {UserEditFormStore} from '../edit-form/UserEditFormStore';

export class UserEditSceneStore {
  @observable.ref
  lookupState: PromiseState<VersionedContentLookup> | null;

  @observable.ref
  timeZonesState: HttpPromiseState<readonly TimeZone[]> | null;

  @observable.ref
  userState: HttpPromiseState<User> | null;

  @observable
  redirectTo: string | null;

  form: UserEditFormStore | null;

  @observable.ref
  saveState: HttpPromiseState<SuccessResponse> | null;

  readonly disposers = new Disposers();

  constructor(readonly userId: number) {
    makeObservable(this);
    this.lookupState = this.timeZonesState = this.userState = null;
    this.form = null;
    this.saveState = null;
    this.redirectTo = null;

    this.setup();
  }

  @action
  loadLookup = () => {
    if (this.lookupState?.isPending === true)
      return;
    this.lookupState = contentLookupService.load();
  };

  @action
  loadTimeZones = () => {
    if (this.timeZonesState?.isPending === true)
      return;
    this.timeZonesState = timeZoneLoaders.list();
  };

  @action
  loadUser = () => {
    if (this.userState?.isPending === true)
      return;
    this.userState = userLoaders.get(this.userId);
  };

  @action
  save = () => {
    if (this.saveState?.isPending === true)
      return;
    if (this.form === null)
      return;
    this.form.markAllTouched();
    logger.log('UserEditStore: form', this.form);
    if (this.form.invalid)
      return;
    this.saveState = userLoaders.update(this.userId, this.form.model);
  };

  @action
  cancel = () => {
    this.redirectTo = routes.userView(this.userId);
  };

  @action
  clearError = () => {
    if (this.saveState?.isRejected === true)
      this.saveState = null;
  };

  private setup() {
    this.disposers.add(reaction(
      () => [
        this.lookupState?.state,
        this.timeZonesState?.state,
        this.userState?.state],
      this.onLoaderStateChange
    ));
    this.disposers.add(reaction(
      () => this.saveState?.state,
      this.onSaveStateChange
    ));
  }

  @action
  private onLoaderStateChange = () => {
    if (this.lookupState?.isResolved !== true ||
      this.timeZonesState?.isResolved !== true ||
      this.userState?.isResolved !== true)
      return;
    this.form = new UserEditFormStore('edit',
      this.lookupState.value.lookup, this.timeZonesState.value);
    this.form.setUser(this.userState.value);

  };

  @action
  private onSaveStateChange = () => {
    if (this.saveState?.isResolved !== true)
      return;
    toastrHelper.success(this.saveState.value);
    this.redirectTo = routes.userView(this.userId);
  };

  dispose = () => {
    this.disposers.dispose();
  };
}
